import {
    Button,
    Chip,
    CircularProgress,
    Drawer,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { Container } from '../../components'
import { useTrainingVideo } from '../../hooks/useTrainingVideo'
import { TrainingVideoCard } from '../../components/TrainingVideoCard/TrainingVideoCard'
import FilterListIcon from '@material-ui/icons/FilterList'
import SearchIcon from '@material-ui/icons/Search'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import { HelpVideo } from '../../navigation/layouts/Lori/types'
import { typographyStyle } from '../../theme/typographyStyle'

// Define sorting types
type SortField = 'title' | 'category' | 'views' | 'added_at'
type SortDirection = 'asc' | 'desc'

export const Help = () => {
    const theme = useTheme()
    const trainingVideoController = useTrainingVideo()
    const containerRef = useRef<HTMLDivElement>(null)

    // State for videos and sorting
    const [sortedVideos, setSortedVideos] = useState<HelpVideo[]>([])
    const [sortField, setSortField] = useState<SortField>('title')
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

    // State for filtering
    const [selectedCategories, setSelectedCategories] = useState<string[]>([])
    const [availableCategories, setAvailableCategories] = useState<string[]>([])
    const [searchQuery, setSearchQuery] = useState<string>('')

    // State for drawer positioning
    const [headerHeight, setHeaderHeight] = useState(56)

    useEffect(() => {
        trainingVideoController.getVideoLibrary()
        trainingVideoController.getCategories()

        // Measure the header height after rendering
        if (containerRef.current) {
            const headerElement = containerRef.current.querySelector(
                '[data-header]',
            )
            if (headerElement) {
                setHeaderHeight(headerElement.getBoundingClientRect().height)
            }
        }
    }, [])

    // Extract unique categories from videos
    useEffect(() => {
        if (trainingVideoController.videoLibrary) {
            const categories = trainingVideoController.videoLibrary
                .map((video) => video.category)
                .filter(
                    (category, index, array) =>
                        array.indexOf(category) === index,
                )
                .sort()

            setAvailableCategories(categories)
        }
    }, [trainingVideoController.videoLibrary])

    // Update sorted and filtered videos whenever filtering/sorting options or video library changes
    useEffect(() => {
        if (trainingVideoController.videoLibrary) {
            // Filter by selected categories and search query
            let filtered = [...trainingVideoController.videoLibrary]

            // Apply search filter
            if (searchQuery.trim().length > 0) {
                const query = searchQuery.toLowerCase().trim()
                filtered = filtered.filter(
                    (video) =>
                        video.title.toLowerCase().includes(query) ||
                        video.category.toLowerCase().includes(query),
                )
            }

            // Apply category filter
            if (selectedCategories.length > 0) {
                filtered = filtered.filter((video) =>
                    selectedCategories.includes(video.category),
                )
            }

            // Then sort the filtered results
            sortVideos(filtered, sortField, sortDirection)
            setSortedVideos(filtered)
        }
    }, [
        trainingVideoController.videoLibrary,
        sortField,
        sortDirection,
        selectedCategories,
        searchQuery,
    ])

    // Function to sort videos based on sortField and sortDirection
    const sortVideos = (
        videos: HelpVideo[],
        field: SortField,
        direction: SortDirection,
    ) => {
        videos.sort((a, b) => {
            let compareResult = 0

            switch (field) {
                case 'title':
                    compareResult = a.title.localeCompare(b.title)
                    break
                case 'category':
                    compareResult = a.category.localeCompare(b.category)
                    break
                case 'views':
                    compareResult = a.views - b.views
                    break
                case 'added_at':
                    compareResult =
                        new Date(a.added_at).getTime() -
                        new Date(b.added_at).getTime()
                    break
                default:
                    compareResult = 0
            }

            return direction === 'asc' ? compareResult : -compareResult
        })
    }

    const [drawerOpen, setDrawerOpen] = useState(false)
    const drawerWidth = 300

    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen)
    }

    // Handle sort toggling
    const handleSortToggle = (field: SortField) => {
        if (sortField === field) {
            // Toggle direction if the same field is clicked
            setSortDirection((prevDirection) =>
                prevDirection === 'asc' ? 'desc' : 'asc',
            )
        } else {
            // Set new field and default to ascending direction
            setSortField(field)
            setSortDirection('asc')
        }
    }

    // Handle category selection change
    const handleCategoryChange = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        setSelectedCategories(event.target.value as string[])
    }

    // Clear all filters
    const clearFilters = () => {
        setSelectedCategories([])
        setSearchQuery('')
        setSortField('title')
        setSortDirection('asc')
    }

    // Get the appropriate icon for sort buttons
    const getSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return null
        }
        return sortDirection === 'asc' ? (
            <ArrowUpwardIcon fontSize="small" />
        ) : (
            <ArrowDownwardIcon fontSize="small" />
        )
    }

    return (
        <Container
            ref={containerRef}
            style={{
                flexDirection: 'column',
                overflowY: 'scroll',
                flex: 1,
                height: '90%',
                position: 'relative',
            }}
        >
            {/* Header */}
            <Container
                data-header
                style={{
                    flexDirection: 'column',
                    marginBottom: theme.spacing(1),
                    paddingBottom: 0,
                }}
            >
                <Container
                    style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h5),
                            fontWeight: 700,
                        }}
                    >
                        Training Video Library
                    </span>
                </Container>
                <Container
                    style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search videos..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        style={{
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        style={{
                            width: 300,
                        }}
                    />
                    <IconButton onClick={toggleDrawer}>
                        <FilterListIcon />
                    </IconButton>
                </Container>
            </Container>

            {/* Videos */}
            <Container
                style={{
                    flexDirection: 'column',
                    padding: theme.spacing(1, 0),
                    width: '100%',
                }}
            >
                {trainingVideoController.loading ? (
                    <Container
                        style={{
                            justifyContent: 'center',
                            padding: theme.spacing(4),
                        }}
                    >
                        <CircularProgress />
                    </Container>
                ) : (
                    sortedVideos.map((video) => (
                        <TrainingVideoCard
                            key={video.id}
                            video={video}
                            onVideoClick={(video) => {
                                window.open(video.video_url, '_blank')
                                trainingVideoController.addVideoView(video.id)
                            }}
                        />
                    ))
                )}
            </Container>
            {/* Filter/Sort Drawer */}

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer}
                variant="persistent"
                ModalProps={{
                    BackdropProps: {
                        style: { opacity: 0 },
                    },
                }}
                PaperProps={{
                    style: {
                        width: drawerWidth,
                        position: 'fixed', // Changed from absolute to fixed
                        height: 'calc(100% - 56px)', // Set height to match page content
                        top: headerHeight + 100, // Use dynamic header height instead of hardcoded value
                        right: 40,
                        boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.1)',
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        zIndex: 1100, // Ensure it's above other content
                    },
                }}
            >
                {/* Drawer Container */}
                <Container
                    style={{
                        width: '100%',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    }}
                >
                    {/* Header */}
                    <Container
                        style={{
                            padding: theme.spacing(1.5, 2),
                            width: '100%',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Button
                            size="small"
                            color="primary"
                            onClick={clearFilters}
                            disabled={
                                selectedCategories.length === 0 &&
                                searchQuery.trim().length === 0 &&
                                sortField === 'title' &&
                                sortDirection === 'asc'
                            }
                        >
                            CLEAR ALL
                        </Button>
                    </Container>

                    {/* Drawer Content */}
                    <Container
                        style={{
                            padding: theme.spacing(2),
                            flexDirection: 'column',
                            width: '100%',
                            overflowY: 'auto',
                            alignItems: 'flex-start',
                            flex: 1,
                        }}
                    >
                        {/* Category Filter */}
                        <Typography
                            variant="subtitle1"
                            style={{
                                fontWeight: 600,
                                marginBottom: theme.spacing(1),
                                marginLeft: theme.spacing(1),
                            }}
                        >
                            Filter by Category
                        </Typography>

                        <FormControl
                            variant="outlined"
                            size="small"
                            style={{
                                width: '90%',
                                marginLeft: theme.spacing(1),
                                marginBottom: theme.spacing(3),
                            }}
                        >
                            <InputLabel id="category-filter-label">
                                Categories
                            </InputLabel>
                            <Select
                                labelId="category-filter-label"
                                id="category-filter"
                                multiple
                                value={selectedCategories}
                                onChange={handleCategoryChange}
                                label="Categories"
                                renderValue={(selected) => (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        {(selected as string[]).map((value) => (
                                            <Chip
                                                key={value}
                                                label={value}
                                                size="small"
                                                style={{ margin: 2 }}
                                            />
                                        ))}
                                    </div>
                                )}
                            >
                                {availableCategories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {category}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Divider */}
                        <div
                            style={{
                                height: 1,
                                backgroundColor: theme.palette.divider,
                                width: '100%',
                                margin: theme.spacing(1, 0, 3),
                            }}
                        />

                        {/* Sort Options */}
                        <Typography
                            variant="subtitle1"
                            style={{
                                fontWeight: 600,
                                marginBottom: theme.spacing(1.5),
                                marginLeft: theme.spacing(1),
                            }}
                        >
                            Sort Videos
                        </Typography>

                        <Container
                            style={{
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                width: '90%',
                                marginLeft: theme.spacing(1),
                            }}
                        >
                            <Button
                                variant={
                                    sortField === 'title'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                color={
                                    sortField === 'title'
                                        ? 'primary'
                                        : 'default'
                                }
                                size="small"
                                onClick={() => handleSortToggle('title')}
                                style={{
                                    margin: theme.spacing(0.5),
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    padding: theme.spacing(1, 2),
                                }}
                                endIcon={getSortIcon('title')}
                            >
                                TITLE
                            </Button>

                            <Button
                                variant={
                                    sortField === 'category'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                color={
                                    sortField === 'category'
                                        ? 'primary'
                                        : 'default'
                                }
                                size="small"
                                onClick={() => handleSortToggle('category')}
                                style={{
                                    margin: theme.spacing(0.5),
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    padding: theme.spacing(1, 2),
                                }}
                                endIcon={getSortIcon('category')}
                            >
                                CATEGORY
                            </Button>

                            <Button
                                variant={
                                    sortField === 'views'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                color={
                                    sortField === 'views'
                                        ? 'primary'
                                        : 'default'
                                }
                                size="small"
                                onClick={() => handleSortToggle('views')}
                                style={{
                                    margin: theme.spacing(0.5),
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    padding: theme.spacing(1, 2),
                                }}
                                endIcon={getSortIcon('views')}
                            >
                                VIEWS
                            </Button>

                            <Button
                                variant={
                                    sortField === 'added_at'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                color={
                                    sortField === 'added_at'
                                        ? 'primary'
                                        : 'default'
                                }
                                size="small"
                                onClick={() => handleSortToggle('added_at')}
                                style={{
                                    margin: theme.spacing(0.5),
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    padding: theme.spacing(1, 2),
                                }}
                                endIcon={getSortIcon('added_at')}
                            >
                                DATE
                            </Button>
                        </Container>

                        {/* Divider */}
                        <div
                            style={{
                                height: 1,
                                backgroundColor: theme.palette.divider,
                                width: '100%',
                                margin: theme.spacing(3, 0, 0),
                            }}
                        />

                        {/* Results Count */}
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{
                                marginTop: theme.spacing(2),
                                marginLeft: theme.spacing(1),
                                alignSelf: 'flex-start',
                            }}
                        >
                            Showing {sortedVideos.length} of{' '}
                            {trainingVideoController.videoLibrary.length} videos
                        </Typography>
                    </Container>
                </Container>
            </Drawer>
        </Container>
    )
}
